import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { ScrollToTop } from "./components/common/ScrollToTop";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import OnboardingGate from "./components/auth/OnboardingGate";
import { useEffect, Suspense, lazy } from "react";
import { UserInfoProvider } from "./context/UserInfoProvider";
import { RoleProvider } from "./context/RoleContext";
import { RequestCountProvider } from "./context/RequestCountContext";
import { FontSizeProvider } from "./context/FontSizeContext";
import { PageLoadingView } from "./components/common/PageLoadingView";
import AnalyticsPageView from "./components/common/AnalyticsPageView";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect as useReactEffect } from "react";
import { useUser } from "./context/UserInfoProvider";
import { isValidInternalRoute } from "./utils/navigation";

// Lazy load all page components for better performance
const NotFound = lazy(() => import("./pages/OtherPage/NotFound"));
const AppLayout = lazy(() => import("./layout/AppLayout"));
const General = lazy(() => import("./pages/GuestRequests/General"));
const InRoomDining = lazy(() => import("./pages/GuestRequests/InRoomDining"));
const Reservations = lazy(() => import("./pages/GuestRequests/Reservations"));
const MessageHistoryPage = lazy(
  () => import("./pages/GuestRequests/MessageHistory")
);
const DataManagement = lazy(
  () => import("./pages/DataManagement/DataManagement")
);
const HotelInfoPage = lazy(
  () => import("./pages/DataManagement/HotelInfoPage")
);
const RestaurantsPage = lazy(
  () => import("./pages/DataManagement/RestaurantsPage")
);
const MenuItemsPage = lazy(
  () => import("./pages/DataManagement/MenuItemsPage")
);
const RoomServicePage = lazy(
  () => import("./pages/DataManagement/RoomServicePage")
);
const HousekeepingPage = lazy(
  () => import("./pages/DataManagement/HousekeepingPage")
);
const FacilitiesPage = lazy(
  () => import("./pages/DataManagement/FacilitiesPage")
);
const SpecialEventsPage = lazy(
  () => import("./pages/DataManagement/SpecialEventsPage")
);
const SpecialProductsPage = lazy(
  () => import("./pages/DataManagement/SpecialProductsPage")
);
const EmployeeManagement = lazy(
  () => import("./pages/EmployeeManagement/EmployeeManagement")
);
const Announcements = lazy(() => import("./pages/Announcements/Announcements"));
// const GuestFeedback = lazy(() => import("./pages/GuestFeedback/GuestFeedback"));
const GuestProfilesPage = lazy(
  () => import("./pages/GuestProfiles/GuestProfilesPage")
);
const GuestApproval = lazy(() => import("./pages/GuestApproval/GuestApproval"));
const ReservationDataIngestionPage = lazy(
  () => import("./pages/ReservationDataIngestion/ReservationDataIngestionPage")
);
const SettingsPage = lazy(() => import("./pages/SettingsPage/SettingsPage"));
const EmployeeProfilePage = lazy(
  () => import("./pages/SettingsPage/EmployeeProfile")
);
const AccessibilitySettingsPage = lazy(
  () => import("./pages/SettingsPage/AccessibilitySettings")
);
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const EmployeeAccessRevoked = lazy(() => import("./pages/EmployeeAccessRevoked"));
const OnboardingPage = lazy(() => import("./pages/Onboarding/Onboarding"));
const RoleBasedRedirect = lazy(
  () => import("./components/auth/RoleBasedRedirect")
);
const TermsPage = lazy(() => import("./pages/Terms"));
const AdvancedAnalytics = lazy(
  () => import("./pages/SettingsPage/AdvancedAnalytics")
);
const AnalyticsOverview = lazy(
  () => import("./pages/SettingsPage/AnalyticsOverview")
);
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicy"));
const StaffCompliancePage = lazy(() => import("./pages/StaffCompliance"));

// Wrapper component that redirects away from /access-denied if user has valid access
const AccessDeniedWrapper = () => {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // If user has valid access, redirect to dashboard
  useEffect(() => {
    if (
      userInfo &&
      userInfo.hotelId &&
      userInfo.employeeId &&
      location.pathname === "/access-denied"
    ) {
      navigate("/", { replace: true });
    }
  }, [userInfo, navigate, location.pathname]);

  return (
    <Suspense fallback={<PageLoadingView />}>
      <AccessDenied />
    </Suspense>
  );
};

export default function App() {
  // Expose notification testing in development
  useEffect(() => {
    if (import.meta.env.DEV) {
      import("./utils/notificationTester").then(
        ({ exposeNotificationTesting }) => {
          exposeNotificationTesting();
        }
      );
    }
  }, []);

  return (
    <Router>
      <UserInfoProvider>
        <FontSizeProvider>
          <RequestCountProvider>
            <RoleProvider>
            <ScrollToTop />
            <AnalyticsPageView />
            <Suspense fallback={<PageLoadingView />}>
              {/* Track and restore last route for persistence */}
              <RouteRestorer />
              <RoutePathTracker />
              <Routes>
                {/* Onboarding route available before dashboard */}
                <Route
                  path="/onboarding"
                  element={
                    <Suspense fallback={<PageLoadingView />}>
                      <OnboardingPage />
                    </Suspense>
                  }
                />

                {/* Access Denied Page - Available without authentication */}
                <Route
                  path="/access-denied"
                  element={
                    <Suspense fallback={<PageLoadingView />}>
                      <AccessDeniedWrapper />
                    </Suspense>
                  }
                />

                {/* Employee Access Revoked Page - Available without authentication */}
                <Route
                  path="/employee-access-revoked"
                  element={
                    <Suspense fallback={<PageLoadingView />}>
                      <EmployeeAccessRevoked />
                    </Suspense>
                  }
                />

                {/* Main App Routes guarded by onboarding completion */}
                <Route element={<OnboardingGate />}>
                  <Route
                    element={
                      <Suspense fallback={<PageLoadingView />}>
                        <AppLayout />
                      </Suspense>
                    }
                  >
                    {/* Role-based redirect for root path */}
                    <Route
                      path="/"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <RoleBasedRedirect />
                        </Suspense>
                      }
                    />

                    <Route
                      path="/general"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <General />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/in-room-dining"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <InRoomDining />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/reservations"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <Reservations />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/message-history"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <MessageHistoryPage />
                        </Suspense>
                      }
                    />

                    {/* Data Management Pages - Route itself accessible to ADMIN, MANAGER, STAFF. Internal tabs are further filtered. */}
                    <Route
                      path="/data-management"
                      element={
                        <ProtectedRoute
                          allowedRoles={["ADMIN", "MANAGER", "STAFF"]}
                        >
                          <DataManagement />
                        </ProtectedRoute>
                      }
                    >
                      <Route
                        index
                        element={
                          <Navigate
                            to="/data-management/hotel-info"
                            replace
                          />
                        }
                      />
                      <Route
                        path="hotel-info"
                        element={
                          <Suspense fallback={<PageLoadingView />}>
                            <HotelInfoPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="restaurants"
                        element={
                          <Suspense fallback={<PageLoadingView />}>
                            <RestaurantsPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="menu-items"
                        element={
                          <Suspense fallback={<PageLoadingView />}>
                            <MenuItemsPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="room-service"
                        element={
                          <Suspense fallback={<PageLoadingView />}>
                            <RoomServicePage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="housekeeping"
                        element={
                          <Suspense fallback={<PageLoadingView />}>
                            <HousekeepingPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="facilities"
                        element={
                          <Suspense fallback={<PageLoadingView />}>
                            <FacilitiesPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="special-events"
                        element={
                          <Suspense fallback={<PageLoadingView />}>
                            <SpecialEventsPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="special-products"
                        element={
                          <Suspense fallback={<PageLoadingView />}>
                            <SpecialProductsPage />
                          </Suspense>
                        }
                      />
                    </Route>

                    {/* Employee Management Pages - ADMIN only */}
                    <Route
                      path="/employee-management"
                      element={
                        <ProtectedRoute allowedRoles={["ADMIN", "HR"]}>
                          <EmployeeManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* Guest Feedback Pages - ADMIN, MANAGER, STAFF - HIDDEN */}
                    {/* <Route
                      path="/guest-feedback"
                      element={
                        <ProtectedRoute
                          allowedRoles={["ADMIN", "MANAGER", "STAFF"]}
                        >
                          <GuestFeedback />
                        </ProtectedRoute>
                      }
                    /> */}

                    {/* Guest Approval Pages */}
                    <Route
                      path="/guest-approval"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <GuestApproval />
                        </Suspense>
                      }
                    />

                    {/* Guest Profiles Pages */}
                    <Route
                      path="/guest-profiles"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <GuestProfilesPage />
                        </Suspense>
                      }
                    />

                    {/* Reservation Data Ingestion Pages - ADMIN, MANAGER */}
                    <Route
                      path="/reservation-data-ingestion"
                      element={
                        <ProtectedRoute allowedRoles={["ADMIN", "MANAGER"]}>
                          <ReservationDataIngestionPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Announcements Page */}
                    <Route
                      path="/announcements"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <Announcements />
                        </Suspense>
                      }
                    />

                    {/* Legal Pages */}
                    <Route
                      path="/terms"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <TermsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/privacy"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <PrivacyPolicyPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/staff-compliance"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <StaffCompliancePage />
                        </Suspense>
                      }
                    />

                    {/* Settings Pages */}
                    <Route
                      path="/settings"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <SettingsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/settings/analytics-overview"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <AnalyticsOverview />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/settings/advanced-analytics"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <AdvancedAnalytics />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/settings/employee-profile"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <EmployeeProfilePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/settings/accessibility"
                      element={
                        <Suspense fallback={<PageLoadingView />}>
                          <AccessibilitySettingsPage />
                        </Suspense>
                      }
                    />
                  </Route>
                </Route>

                {/* Fallback Route */}
                <Route
                  path="*"
                  element={
                    <Suspense fallback={<PageLoadingView />}>
                      <NotFound />
                    </Suspense>
                  }
                />
              </Routes>
            </Suspense>
            <Analytics />
          </RoleProvider>
        </RequestCountProvider>
        </FontSizeProvider>
      </UserInfoProvider>
    </Router>
  );
}

// Tracks the current route and stores it for persistence after auth reloads
function RoutePathTracker() {
  const location = useLocation();
  useReactEffect(() => {
    try {
      sessionStorage.setItem(
        "selfserve_last_route",
        location.pathname + location.search
      );
    } catch (e) {
      void e;
    }
  }, [location.pathname, location.search]);
  return null;
}

// On initial mount inside the app router, if we're at root but have a stored last route, navigate there
function RouteRestorer() {
  const location = useLocation();
  const navigate = useNavigate();
  useReactEffect(() => {
    if (location.pathname === "/") {
      try {
        const last = sessionStorage.getItem("selfserve_last_route");
        // Validate the route before using it to prevent open redirect vulnerabilities
        if (last && last !== "/" && isValidInternalRoute(last)) {
          navigate(last, { replace: true });
        }
      } catch (e) {
        void e;
      }
    }
    // run once on mount
  }, []);
  return null;
}
